import React, { useEffect, useMemo, useRef, useState } from "react";
import jwt_decode from "jwt-decode";
import { postVenta, putVenta } from "../helpers/rutaVentas";
import { getClientes } from "../helpers/rutaClientes";
import { getClientesRecepcion as getClientesReciben } from "../helpers/rutaClienterecepcion";
import { getProductos } from "../helpers/rutaProductos";
import {
  getUsuariosColaboradores,
} from "../helpers/rutaUsuarios";

const clean = (v) => String(v ?? "").trim();

const todayLocalISO = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const toInputDate = (value) => {
  if (!value) return todayLocalISO();

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return todayLocalISO();

  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);

  return local.toISOString().slice(0, 10);
};

const toNumber = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const formatUSD = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "US$ 0.00";

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "object") return clean(v._id ?? v.id ?? v.value ?? "");
  return clean(v);
};

const unwrapClientes = (r) => {
  if (Array.isArray(r?.clientes)) return r.clientes;
  if (Array.isArray(r?.clientesReciben)) return r.clientesReciben;
  if (Array.isArray(r?.clientesRecibe)) return r.clientesRecibe;
  if (Array.isArray(r?.receptores)) return r.receptores;

  if (Array.isArray(r?.data?.clientes)) return r.data.clientes;
  if (Array.isArray(r?.data?.clientesReciben)) return r.data.clientesReciben;
  if (Array.isArray(r?.data?.clientesRecibe)) return r.data.clientesRecibe;
  if (Array.isArray(r?.data?.receptores)) return r.data.receptores;

  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;

  return [];
};

const unwrapProductos = (r) => {
  if (Array.isArray(r?.productos)) return r.productos;
  if (Array.isArray(r?.data?.productos)) return r.data.productos;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const unwrapUsuarios = (r) => {
  if (Array.isArray(r?.usuarios)) return r.usuarios;
  if (Array.isArray(r?.data?.usuarios)) return r.data.usuarios;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;

  return [];
};

const nombreUsuarioVenta = (u = {}) => {
  if (!u || typeof u !== "object") return "-";

  return (
    [u.nombres, u.apellidos].map(clean).filter(Boolean).join(" ") ||
    [u.nombre, u.apellido].map(clean).filter(Boolean).join(" ") ||
    clean(u.email) ||
    "-"
  );
};

const nombreCliente = (c = {}) => {
  const armado = [
    c?.apellido1,
    c?.apellido2,
    c?.nombre,
    c?.segundoNombre,
  ]
    .map(clean)
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

  return (
    armado ||
    clean(c?.nombreCompleto) ||
    clean(c?.razonsocial) ||
    clean(c?.dni) ||
    clean(c?.carnetIdentidad) ||
    "-"
  );
};

const nombreProducto = (p = {}) => {
  return (
    clean(p?.nombreComercial) ||
    clean(p?.modelo) ||
    clean(p?.descripcion) ||
    "-"
  );
};

const getProductoPrecio = (p = {}) => toNumber(p?.precioVentaUSD, 0);
const getProductoPrecioMinimo = (p = {}) => toNumber(p?.precioMinimoUSD, 0);
const getProductoEnvio = (p = {}) => toNumber(p?.costoEnvioUSD, 0);

const getColorId = (c) => getId(c);

const getColorNombre = (c = {}) =>
  clean(c?.nombre) ||
  clean(c?.color) ||
  clean(c?.descripcion) ||
  clean(c?.codigoHex) ||
  "-";

const estadoInicial = {
  fecha: todayLocalISO(),
  clienteCompra: "",
  colaboradorVenta: "",
  observaciones: "",
  estado: "PENDIENTE",
};

const itemInicial = {
  producto: "",
  clienteRecibe: "",
  cantidad: 1,
  precioVentaUSD: "",
  costoEnvioUSD: "",
  coloresPreferidos: [],
  colorEntregado: "",
  observacionColor: "",
  autorizacionPrecioTexto: "",
  aclaracion: "",
  estadoItem: "PENDIENTE",
  observaciones: "",
};

const getUsuarioLogueado = () => {
  const token = localStorage.getItem("token") || "";

  try {
    if (!token) return {};

    const decoded = jwt_decode(token);
    return decoded?.usuario || decoded?.user || decoded || {};
  } catch {
    return {};
  }
};

const esRolVendedor = () => {
  const usuario = getUsuarioLogueado();
  return usuario?.role === "VENDEDOR_ROLE";
};

const AddVentaForm = ({
  setShow,
  modo = "alta",
  ventaEditar = null,
  onSaved,
  compacto = false,
}) => {

  const esEdicion = modo === "editar" && !!ventaEditar?._id;

  const esVendedor = esRolVendedor();

  const [formValues, setFormValues] = useState(estadoInicial);
  const [items, setItems] = useState([{ ...itemInicial }]);

  const [clientesCompra, setClientesCompra] = useState([]);
  const [clientesRecibe, setClientesRecibe] = useState([]);
  const [productos, setProductos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const [qClienteCompra, setQClienteCompra] = useState("");
  const [qClienteRecibe, setQClienteRecibe] = useState("");
  const [qProducto, setQProducto] = useState("");
  const [qColaborador, setQColaborador] = useState("");

  const formTopRef = useRef(null);

  useEffect(() => {
    let alive = true;

    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError("");

        const [
          respClientesCompra,
          respClientesRecibe,
          respProductos,
          respUsuarios,
        ] = await Promise.all([
          getClientes({ limit: 10000 }),
          getClientesReciben({ limit: 10000 }),
          getProductos({ limit: 10000 }),

          // El vendedor no puede consultar /usuarios.
          // Se evita el 403 y se oculta colaborador.
          esVendedor
            ? Promise.resolve({ usuarios: [] })
            : getUsuariosColaboradores(),
        ]);

        if (!alive) return;

        const clientesCompraList = unwrapClientes(respClientesCompra)
          .filter((c) => c?.activo ?? true)
          .sort((a, b) =>
            nombreCliente(a).localeCompare(nombreCliente(b), "es", {
              sensitivity: "base",
            })
          );

        const clientesRecibeList = unwrapClientes(respClientesRecibe)
          .filter((c) => c?.activo ?? true)
          .sort((a, b) =>
            nombreCliente(a).localeCompare(nombreCliente(b), "es", {
              sensitivity: "base",
            })
          );

        const productosList = unwrapProductos(respProductos)
          .filter((p) => p?.activo ?? true)
          .sort((a, b) =>
            nombreProducto(a).localeCompare(nombreProducto(b), "es", {
              sensitivity: "base",
            })
          );
        
        const usuariosList = esVendedor
          ? []
          : unwrapUsuarios(respUsuarios)
              .filter((u) => u?.activo ?? true)
              .sort((a, b) =>
                nombreUsuarioVenta(a).localeCompare(nombreUsuarioVenta(b), "es", {
                  sensitivity: "base",
                })
              );

        setClientesCompra(clientesCompraList);
        setClientesRecibe(clientesRecibeList);
        setProductos(productosList);
        setUsuarios(usuariosList);

      } catch (err) {
        console.error("❌ Error cargando datos para venta:", err);

        if (alive) {
          setError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "No se pudieron cargar los datos para la venta."
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    cargarDatos();

    return () => {
      alive = false;
    };
  }, []);

  const productosMap = useMemo(() => {
    const map = {};

    productos.forEach((p) => {
      const id = getId(p);
      if (id) map[id] = p;
    });

    return map;
  }, [productos]);

  const normalizarTexto = (v) =>
  clean(v)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();

  const resolverProductoIdItem = (item = {}) => {
    const idDirecto = getId(item.producto);

    const modeloItem = normalizarTexto(item.modeloProducto);
    const descripcionItem = normalizarTexto(item.descripcionProducto);
    const textoItem = descripcionItem || modeloItem;

    const coincideTexto = (p = {}) => {
      const modeloProd = normalizarTexto(p.modelo);
      const nombreProd = normalizarTexto(p.nombreComercial);
      const descripcionProd = normalizarTexto(p.descripcion);
      const nombreGeneral = normalizarTexto(nombreProducto(p));

      const camposProducto = [
        modeloProd,
        nombreProd,
        descripcionProd,
        nombreGeneral,
      ].filter(Boolean);

      if (
        descripcionItem &&
        camposProducto.some((txt) => txt === descripcionItem)
      ) {
        return true;
      }

      if (modeloItem && camposProducto.some((txt) => txt === modeloItem)) {
        return true;
      }

      if (
        descripcionItem &&
        descripcionItem.length >= 5 &&
        camposProducto.some(
          (txt) => txt.includes(descripcionItem) || descripcionItem.includes(txt)
        )
      ) {
        return true;
      }

      if (
        modeloItem &&
        modeloItem.length >= 5 &&
        camposProducto.some(
          (txt) => txt.includes(modeloItem) || modeloItem.includes(txt)
        )
      ) {
        return true;
      }

      return false;
    };

    // 1) Primero busco por el texto real guardado en el ítem.
    // Esto evita confiar en un ObjectId viejo o cruzado.
    const porTexto = productos.find((p) => coincideTexto(p));

    if (porTexto?._id) {
      return porTexto._id;
    }

    // 2) Después pruebo por código.
    const codItem = Number(item.codprod || 0);

    if (codItem > 0) {
      const porCodigo = productos.find(
        (p) => Number(p.codprod || 0) === codItem
      );

      if (porCodigo?._id) {
        return porCodigo._id;
      }
    }

    // 3) Recién al final uso el id directo.
    return idDirecto || "";
  };

  const productoExtraDesdeItem = (item = {}) => {
    const id = resolverProductoIdItem(item);

    if (!id) return null;

    const existente = productos.find((p) => getId(p) === id);

    if (existente) return existente;

    const prodObj =
      item.producto && typeof item.producto === "object" ? item.producto : {};

    return {
      _id: id,
      codprod: item.codprod || prodObj.codprod || "",
      nombreComercial:
        clean(item.descripcionProducto) ||
        clean(prodObj.nombreComercial) ||
        clean(item.modeloProducto) ||
        "PRODUCTO DE LA VENTA",
      modelo: clean(item.modeloProducto) || clean(prodObj.modelo) || "",
      descripcion:
        clean(item.descripcionProducto) || clean(prodObj.descripcion) || "",
      precioVentaUSD: item.precioVentaUSD ?? prodObj.precioVentaUSD ?? 0,
      precioMinimoUSD: item.precioMinimoUSD ?? prodObj.precioMinimoUSD ?? 0,
      costoEnvioUSD: item.costoEnvioUSD ?? prodObj.costoEnvioUSD ?? 0,
      usaColor: prodObj.usaColor ?? false,
      coloresDisponibles: prodObj.coloresDisponibles || [],
    };
  };

    const cargarVentaEnFormulario = () => {
      if (!esEdicion || !ventaEditar) return;

      setFormValues({
        fecha: toInputDate(ventaEditar.fecha),
        clienteCompra: getId(ventaEditar.clienteCompra),
        colaboradorVenta: getId(ventaEditar.colaboradorVenta),
        observaciones: clean(ventaEditar.observaciones),
        estado: clean(ventaEditar.estado || "PENDIENTE").toUpperCase(),
      });

      const itemsEditados = Array.isArray(ventaEditar.items)
        ? ventaEditar.items.map((item) => ({
            producto: resolverProductoIdItem(item),
            clienteRecibe: getId(item.clienteRecibe),

            cantidad: item.cantidad ?? 1,
            precioVentaUSD: item.precioVentaUSD ?? "",
            costoEnvioUSD: item.costoEnvioUSD ?? "",

            coloresPreferidos: Array.isArray(item.coloresPreferidos)
              ? item.coloresPreferidos.map(getId).filter(Boolean)
              : [],

            colorEntregado: getId(item.colorEntregado),

            observacionColor: clean(item.observacionColor),
            autorizacionPrecioTexto: clean(item.autorizacionPrecioTexto),
            aclaracion: clean(item.aclaracion),
            estadoItem: clean(item.estadoItem || "PENDIENTE").toUpperCase(),
            observaciones: clean(item.observaciones),
          }))
        : [];

      setItems(itemsEditados.length > 0 ? itemsEditados : [{ ...itemInicial }]);
    };

  useEffect(() => {
    if (!esEdicion) return;
    if (loading) return;

    cargarVentaEnFormulario();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esEdicion, loading, ventaEditar?._id]);

  const clientesCompraFiltrados = useMemo(() => {
  const q = clean(qClienteCompra).toLowerCase();

  const base = !q
    ? clientesCompra.slice(0, 80)
    : clientesCompra
        .filter((c) => {
          const texto = [
            nombreCliente(c),
            c?.dni,
            c?.telefono1,
            c?.email,
            c?.ciudad,
            c?.estado,
            c?.zipCode,
          ]
            .map(clean)
            .join(" ")
            .toLowerCase();

          return texto.includes(q);
        })
        .slice(0, 80);

  if (!esEdicion) {
    return base;
  }

  const clienteCompraId = getId(
    ventaEditar?.clienteCompra
  );

  if (
    !clienteCompraId ||
    base.some(
      (c) => getId(c) === clienteCompraId
    )
  ) {
    return base;
  }

  const clienteOriginal =
    clientesCompra.find(
      (c) => getId(c) === clienteCompraId
    ) ||
    (typeof ventaEditar?.clienteCompra ===
    "object"
      ? ventaEditar.clienteCompra
      : null);

  return clienteOriginal
    ? [clienteOriginal, ...base]
    : base;
}, [
  clientesCompra,
  qClienteCompra,
  esEdicion,
  ventaEditar,
]);

const clientesRecibeFiltrados = useMemo(() => {
  const q = clean(qClienteRecibe).toLowerCase();

  const base = !q
    ? clientesRecibe.slice(0, 80)
    : clientesRecibe
        .filter((c) => {
          const texto = [
            nombreCliente(c),
            c?.dni,
            c?.carnetIdentidad,
            c?.telefono1,
            c?.telefonoPrincipal,
            c?.email,
            c?.ciudad,
            c?.estado,
            c?.zipCode,
            c?.provincia,
            c?.municipio,
            c?.direccion,
            c?.domicilio,
          ]
            .map(clean)
            .join(" ")
            .toLowerCase();

          return texto.includes(q);
        })
        .slice(0, 80);

  if (
    !esEdicion ||
    !Array.isArray(ventaEditar?.items)
  ) {
    return base;
  }

  const map = new Map();

  base.forEach((c) => {
    const id = getId(c);
    if (id) map.set(id, c);
  });

  ventaEditar.items.forEach((item) => {
    const id = getId(item?.clienteRecibe);

    if (!id || map.has(id)) return;

    const clienteOriginal =
      clientesRecibe.find(
        (c) => getId(c) === id
      ) ||
      (typeof item?.clienteRecibe ===
      "object"
        ? item.clienteRecibe
        : null);

    if (clienteOriginal) {
      map.set(id, clienteOriginal);
    }
  });

  return Array.from(map.values());
}, [
  clientesRecibe,
  qClienteRecibe,
  esEdicion,
  ventaEditar,
]);

  const usuariosFiltrados = useMemo(() => {
    const q = clean(qColaborador).toLowerCase();

    if (!q) return usuarios.slice(0, 80);

    return usuarios
      .filter((u) => {
        const texto = [
          nombreUsuarioVenta(u),
          u?.email,
          u?.role,
          u?.dni,
        ]
          .map(clean)
          .join(" ")
          .toLowerCase();

        return texto.includes(q);
      })
      .slice(0, 80);
  }, [usuarios, qColaborador]);

  const productosFiltrados = useMemo(() => {
    const q = clean(qProducto).toLowerCase();

    if (!q) return productos.slice(0, 120);

    return productos
      .filter((p) => {
        const categoria =
          typeof p?.categoria === "object"
            ? p?.categoria?.nombre || p?.categoria?.descripcion
            : "";

        const marca =
          typeof p?.marca === "object" ? p?.marca?.nombre || p?.marca?.marca : "";

        const texto = [
          p?.codprod,
          p?.nombreComercial,
          p?.modelo,
          p?.descripcion,
          categoria,
          marca,
          p?.tipoMotor,
        ]
          .map(clean)
          .join(" ")
          .toLowerCase();

        return texto.includes(q);
      })
      .slice(0, 120);
  }, [productos, qProducto]);

  const productosExtraEdicion = useMemo(() => {
    if (!esEdicion || !Array.isArray(ventaEditar?.items)) return [];

    return ventaEditar.items
      .map((item) => productoExtraDesdeItem(item))
      .filter(Boolean);
  }, [esEdicion, ventaEditar, productos]);

  const productosVisibles = useMemo(() => {
    const map = new Map();

    productosFiltrados.forEach((prod) => {
      const id = getId(prod);
      if (id) map.set(id, prod);
    });

    productosExtraEdicion.forEach((prod) => {
      const id = getId(prod);
      if (id && !map.has(id)) map.set(id, prod);
    });

    items.forEach((item) => {
      const id = item.producto;
      if (!id || map.has(id)) return;

      const encontrado = productos.find((prod) => getId(prod) === id);

      if (encontrado) {
        map.set(id, encontrado);
      }
    });

    return Array.from(map.values());
  }, [productosFiltrados, productosExtraEdicion, productos, items]);

  const totales = useMemo(() => {
    let totalProductosUSD = 0;
    let totalEnvioUSD = 0;
    let totalUSD = 0;
    let totalComisionUSD = 0;
    let tieneBajoMinimo = false;

    items.forEach((it) => {
      const producto = productosMap[it.producto] || {};

      const cantidad = toNumber(it.cantidad, 0);
      const precioVentaUSD = toNumber(it.precioVentaUSD, 0);
      const costoEnvioUSD = toNumber(it.costoEnvioUSD, 0);
      const precioMinimoUSD = getProductoPrecioMinimo(producto);
      const comisionBaseUSD = toNumber(producto?.comisionBaseUSD, 0);

      totalProductosUSD += cantidad * precioVentaUSD;
      totalEnvioUSD += cantidad * costoEnvioUSD;
      totalUSD += cantidad * precioVentaUSD + cantidad * costoEnvioUSD;
      totalComisionUSD += cantidad * comisionBaseUSD;

      if (precioMinimoUSD > 0 && precioVentaUSD < precioMinimoUSD) {
        tieneBajoMinimo = true;
      }
    });

    return {
      totalProductosUSD,
      totalEnvioUSD,
      totalUSD,
      totalComisionUSD,
      tieneBajoMinimo,
    };
  }, [items, productosMap]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: name === "estado" ? String(value || "").toUpperCase() : value,
    }));
  };

  const handleItemChange = (idx, name, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;

        if (name === "producto") {
          const prod = productosMap[value] || {};

          return {
            ...item,
            producto: value,
            precioVentaUSD: getProductoPrecio(prod),
            costoEnvioUSD: getProductoEnvio(prod),
            coloresPreferidos: [],
            colorEntregado: "",
            observacionColor: "",
            autorizacionPrecioTexto: "",
            aclaracion: prod?.aclaracion || "",
          };
        }

        if (name === "cantidad") {
          return {
            ...item,
            cantidad: value,
          };
        }

        if (name === "precioVentaUSD") {
          const prod = productosMap[item.producto] || {};
          const precioMinimo = getProductoPrecioMinimo(prod);
          const precio = toNumber(value, 0);

          return {
            ...item,
            precioVentaUSD: value,
            autorizacionPrecioTexto:
              precioMinimo > 0 && precio < precioMinimo
                ? item.autorizacionPrecioTexto ||
                  "REQUIERE AUTORIZACIÓN TELEFÓNICA"
                : item.autorizacionPrecioTexto,
          };
        }

        return {
          ...item,
          [name]: value,
        };
      })
    );
  };

  const toggleColorPreferido = (idx, colorId) => {
    if (!colorId) return;

    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;

        const actual = Array.isArray(item.coloresPreferidos)
          ? item.coloresPreferidos
          : [];

        const existe = actual.includes(colorId);

        return {
          ...item,
          coloresPreferidos: existe
            ? actual.filter((x) => x !== colorId)
            : [...actual, colorId],
        };
      })
    );
  };

  const agregarItem = () => {
    setItems((prev) => [...prev, { ...itemInicial }]);
  };

  const quitarItem = (idx) => {
    setItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  };

  const validar = () => {
    if (!clean(formValues.fecha)) return "Completá la fecha.";
    if (!clean(formValues.clienteCompra)) {
      return "Seleccioná el cliente que compra.";
    }

    if (!Array.isArray(items) || items.length === 0) {
      return "La venta debe tener al menos un producto.";
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const nro = i + 1;

      if (!clean(item.producto)) {
        return `Seleccioná el producto del ítem ${nro}.`;
      }

      if (!clean(item.clienteRecibe)) {
        return `Seleccioná el cliente que recibe del ítem ${nro}.`;
      }

      if (toNumber(item.cantidad, 0) <= 0) {
        return `La cantidad del ítem ${nro} debe ser mayor a cero.`;
      }

      if (toNumber(item.precioVentaUSD, 0) <= 0) {
        return `El precio de venta del ítem ${nro} debe ser mayor a cero.`;
      }

      const producto =
        productosMap[item.producto] || {};

      const precioMinimo =
        getProductoPrecioMinimo(producto);

      const precioVenta =
        toNumber(
          item.precioVentaUSD,
          0
        );

      /*
        =========================================================
        EDICIÓN DE VENTA HISTÓRICA
        =========================================================

        Si la venta ya existía y el precio del producto
        NO fue modificado, respetamos el precio histórico.

        Esto permite modificar datos operativos como:
        - colaborador
        - receptor
        - observaciones
        - estado

        sin obligar a autorizar nuevamente un precio antiguo
        que hoy quedó debajo del mínimo actual.
      */
      const itemOriginal =
        esEdicion &&
        Array.isArray(
          ventaEditar?.items
        )
          ? ventaEditar.items[i]
          : null;

      const productoOriginalId =
        itemOriginal
          ? resolverProductoIdItem(
              itemOriginal
            )
          : "";

      const precioOriginal =
        toNumber(
          itemOriginal?.precioVentaUSD,
          -1
        );

      const precioHistoricoSinCambios =
        esEdicion &&
        !!itemOriginal &&
        productoOriginalId ===
          item.producto &&
        Math.abs(
          precioVenta -
            precioOriginal
        ) < 0.01;

      if (
        precioMinimo > 0 &&
        precioVenta < precioMinimo &&
        !precioHistoricoSinCambios
      ) {
        if (
          !clean(
            item.autorizacionPrecioTexto
          )
        ) {
          return `El ítem ${nro} está debajo del precio mínimo. Indicá autorización.`;
        }
      }
    }

    return "";
  };

  const productoIdParaPayload = (
    item,
    idx
  ) => {
    if (
      !esEdicion ||
      !Array.isArray(
        ventaEditar?.items
      )
    ) {
      return item.producto;
    }

    const itemOriginal =
      ventaEditar.items[idx];

    if (!itemOriginal) {
      return item.producto;
    }

    /*
      ID que el formulario mostró al cargar
      la venta histórica.
    */
    const productoOriginalUI =
      resolverProductoIdItem(
        itemOriginal
      );

    /*
      ID REAL que quedó grabado
      históricamente en MongoDB.
    */
    const productoOriginalReal =
      getId(
        itemOriginal.producto
      );

    /*
      Si el usuario NO cambió el producto,
      mandamos nuevamente el ID histórico real.

      Así el backend no interpreta que hubo
      una modificación económica.
    */
    if (
      productoOriginalReal &&
      item.producto ===
        productoOriginalUI
    ) {
      return productoOriginalReal;
    }

    /*
      Si realmente seleccionó otro producto,
      mandamos el nuevo ID y el backend
      hará su control normal.
    */
    return item.producto;
  };

  const armarPayload = () => ({
    fecha: formValues.fecha,
    clienteCompra: formValues.clienteCompra,
    colaboradorVenta: esVendedor ? "" : formValues.colaboradorVenta,
    estado: clean(formValues.estado || "PENDIENTE").toUpperCase(),
    observaciones: clean(formValues.observaciones).toUpperCase(),

    items: items.map((item, idx) => ({
      producto:
        productoIdParaPayload(
          item,
          idx
        ),
      clienteRecibe: item.clienteRecibe,

      cantidad: toNumber(item.cantidad, 1),
      precioVentaUSD: toNumber(item.precioVentaUSD, 0),
      costoEnvioUSD:
        item.costoEnvioUSD === "" || item.costoEnvioUSD === null
          ? undefined
          : toNumber(item.costoEnvioUSD, 0),

      coloresPreferidos: Array.isArray(item.coloresPreferidos)
        ? item.coloresPreferidos
        : [],

      colorEntregado: item.colorEntregado || "",
      observacionColor: clean(item.observacionColor).toUpperCase(),

      autorizacionPrecioTexto: clean(
        item.autorizacionPrecioTexto
      ).toUpperCase(),

      aclaracion: clean(item.aclaracion).toUpperCase(),
      estadoItem: clean(item.estadoItem || "PENDIENTE").toUpperCase(),
      observaciones: clean(item.observaciones).toUpperCase(),
    })),
  });

  const resetForm = () => {
    setFormValues({
      ...estadoInicial,
      fecha: todayLocalISO(),
    });

    setItems([{ ...itemInicial }]);
    setQClienteCompra("");
    setQClienteRecibe("");
    setQProducto("");
    setQColaborador("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setOk("");

    const msg = validar();

    if (msg) {
      setError(msg);

      setTimeout(() => {
        formTopRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);

      return;
    }

    try {
      setSaving(true);

      const payload = armarPayload();
      
      console.log("📦 Payload venta:", payload);
      console.log("🟡 Modo edición:", esEdicion, "ID:", ventaEditar?._id);

      const resp = esEdicion
        ? await putVenta(ventaEditar._id, payload)
        : await postVenta(payload);

      if (resp?.ok === false) {
        setError(resp?.error || "No se pudo guardar la venta.");
        return;
      }

      const ventaResp = resp?.venta || resp?.data?.venta || resp?.data || null;
      const nro = ventaResp?.nroventa ? ` N° ${ventaResp.nroventa}` : "";

      setOk(
        esEdicion
          ? `Venta modificada correctamente.${nro}`
          : `Venta guardada correctamente.${nro}`
      );

      if (typeof onSaved === "function") {
        onSaved(ventaResp);
      }

      if (!esEdicion) {
        resetForm();
      }

      setTimeout(() => {
        if (typeof setShow === "function") setShow(false);
      }, 900);
    } catch (err) {
      console.error("❌ Error guardando venta:", err?.response?.data || err);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo guardar la venta."
      );
    } finally {
      setSaving(false);
    }
  };

  const volver = () => {
    if (typeof setShow === "function") setShow(false);
  };

  if (loading) {
    return (
      <div className={compacto ? "container-fluid p-0" : "container mt-4 mb-5"}>
        <div className="alert alert-info">Cargando datos de ventas...</div>
      </div>
    );
  }

  return (
    <div className={compacto ? "container-fluid p-0" : "container mt-4 mb-5"}>
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            {esEdicion ? `Modificar Venta N° ${ventaEditar?.nroventa || ""}` : "Alta de Venta"}
          </h5>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="card-body" ref={formTopRef}>
            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}

            {ok && (
              <div className="alert alert-success py-2" role="alert">
                {ok}
              </div>
            )}

            <h6 className="border-bottom pb-2 mb-3">Datos principales</h6>

            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Fecha *</label>
                <input
                  type="date"
                  className="form-control"
                  name="fecha"
                  value={formValues.fecha}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Estado</label>
                <select
                  className="form-control"
                  name="estado"
                  value={formValues.estado}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="PENDIENTE">PENDIENTE</option>
                  <option value="CONFIRMADA">CONFIRMADA</option>
                  <option value="EN_PROCESO">EN PROCESO</option>
                  <option value="ENVIADA">ENVIADA</option>
                  <option value="ENTREGADA">ENTREGADA</option>
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Buscar cliente que compra</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre / documento / teléfono / email"
                  value={qClienteCompra}
                  onChange={(e) => setQClienteCompra(e.target.value)}
                  disabled={saving}
                  autoComplete="off"
                />
              </div>

              <div className="col-md-12">
                <label className="form-label">Cliente que compra *</label>
                <select
                  className="form-control"
                  name="clienteCompra"
                  value={formValues.clienteCompra}
                  onChange={handleChange}
                  disabled={saving}
                >
                  <option value="">Elija la opción adecuada</option>

                  {clientesCompraFiltrados.map((cliente) => (
                    <option key={cliente._id} value={cliente._id}>
                      {nombreCliente(cliente)}
                      {cliente.dni ? ` - ${cliente.dni}` : ""}
                      {cliente.telefono1 ? ` - ${cliente.telefono1}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {!esVendedor && (
                <>
                  <div className="col-md-4">
                    <label>Buscar colaborador</label>
                    <input
                      type="text"
                      className="form-control"
                      value={qColaborador}
                      onChange={(e) => setQColaborador(e.target.value)}
                      placeholder="Nombre / email / rol"
                    />
                  </div>

                  <div className="col-md-8">
                    <label>Colaborador / compañero</label>
                    <select
                      className="form-control"
                      value={formValues.colaboradorVenta}
                      onChange={(e) =>
                        setFormValues((prev) => ({
                          ...prev,
                          colaboradorVenta: e.target.value,
                        }))
                      }
                    >
                      <option value="">Sin colaborador</option>

                      {usuariosFiltrados.map((u) => (
                        <option key={u._id} value={u._id}>
                          {nombreUsuarioVenta(u)}
                        </option>
                      ))}
                    </select>

                    <small className="text-muted">
                      Opcional. Usar solo si otro usuario colabora en la venta.
                    </small>
                  </div>
                </>
              )}


              <div className="col-md-12">
                <label className="form-label">Observaciones generales</label>
                <textarea
                  className="form-control"
                  name="observaciones"
                  rows="2"
                  value={formValues.observaciones}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
            </div>

            <h6 className="border-bottom pb-2 mt-4 mb-3">Productos vendidos</h6>

            <div className="row g-3 mb-2">
              <div className="col-md-6">
                <label className="form-label">Buscar producto</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Producto / modelo / código / categoría / marca"
                  value={qProducto}
                  onChange={(e) => setQProducto(e.target.value)}
                  disabled={saving}
                  autoComplete="off"
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">Buscar cliente que recibe</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre / documento / teléfono / email"
                  value={qClienteRecibe}
                  onChange={(e) => setQClienteRecibe(e.target.value)}
                  disabled={saving}
                  autoComplete="off"
                />
              </div>
            </div>

            {items.map((item, idx) => {
              const producto = productosMap[item.producto] || {};
              const usaColor = !!producto?.usaColor;
              const coloresDisponibles = Array.isArray(
                producto?.coloresDisponibles
              )
                ? producto.coloresDisponibles
                : [];

              const cantidad = toNumber(item.cantidad, 0);
              const precio = toNumber(item.precioVentaUSD, 0);
              const envio = toNumber(item.costoEnvioUSD, 0);
              const subtotal = cantidad * precio + cantidad * envio;

              const precioMinimo = getProductoPrecioMinimo(producto);
              const bajoMinimo =
                precioMinimo > 0 && precio > 0 && precio < precioMinimo;

              return (
                <div className="card mb-3" key={`item-${idx}`}>
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Ítem {idx + 1}</strong>

                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => quitarItem(idx)}
                      disabled={saving || items.length <= 1}
                    >
                      Quitar
                    </button>
                  </div>

                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Producto *</label>
                        <select
                          className="form-control"
                          value={item.producto}
                          onChange={(e) =>
                            handleItemChange(idx, "producto", e.target.value)
                          }
                          disabled={saving}
                        >
                          <option value="">Elija la opción adecuada</option>

                          {productosVisibles.map((prod) => (
                            <option key={prod._id} value={prod._id}>
                              {prod.codprod ? `${prod.codprod} - ` : ""}
                              {nombreProducto(prod)}
                              {prod.precioVentaUSD
                                ? ` - ${formatUSD(prod.precioVentaUSD)}`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Cliente que recibe *
                        </label>
                        <select
                          className="form-control"
                          value={item.clienteRecibe}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "clienteRecibe",
                              e.target.value
                            )
                          }
                          disabled={saving}
                        >
                          <option value="">Elija la opción adecuada</option>
                            {clientesRecibeFiltrados.map((cliente) => (
                              <option key={cliente._id} value={cliente._id}>
                                {nombreCliente(cliente)}
                                {cliente.dni || cliente.carnetIdentidad
                                  ? ` - ${cliente.dni || cliente.carnetIdentidad}`
                                  : ""}
                                {cliente.telefono1 || cliente.telefonoPrincipal
                                  ? ` - ${cliente.telefono1 || cliente.telefonoPrincipal}`
                                  : ""}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Cantidad *</label>
                        <input
                          type="number"
                          className="form-control"
                          min="1"
                          value={item.cantidad}
                          onChange={(e) =>
                            handleItemChange(idx, "cantidad", e.target.value)
                          }
                          disabled={saving}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Precio Venta USD *</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          step="0.01"
                          value={item.precioVentaUSD}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "precioVentaUSD",
                              e.target.value
                            )
                          }
                          disabled={saving}
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="form-label">Costo Envío USD</label>
                        <input
                          type="number"
                          className="form-control"
                          min="0"
                          step="0.01"
                          value={item.costoEnvioUSD}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "costoEnvioUSD",
                              e.target.value
                            )
                          }
                          disabled={saving}
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Subtotal</label>
                        <input
                          className="form-control"
                          value={formatUSD(subtotal)}
                          readOnly
                        />
                      </div>

                      <div className="col-md-2">
                        <label className="form-label">Estado Ítem</label>
                        <select
                          className="form-control"
                          value={item.estadoItem}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "estadoItem",
                              e.target.value
                            )
                          }
                          disabled={saving}
                        >
                          <option value="PENDIENTE">PENDIENTE</option>
                          <option value="CONFIRMADO">CONFIRMADO</option>
                          <option value="EN_PROCESO">EN PROCESO</option>
                          <option value="ENVIADO">ENVIADO</option>
                          <option value="ENTREGADO">ENTREGADO</option>
                          <option value="ANULADO">ANULADO</option>
                        </select>
                      </div>

                      {bajoMinimo && (
                        <div className="col-md-12">
                          <div className="alert alert-warning py-2 mb-0">
                            Este producto está vendido por debajo del precio
                            mínimo ({formatUSD(precioMinimo)}). Debe quedar
                            autorización registrada.
                          </div>
                        </div>
                      )}

                      {bajoMinimo && (
                        <div className="col-md-12">
                          <label className="form-label">
                            Autorización por precio bajo
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.autorizacionPrecioTexto}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "autorizacionPrecioTexto",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          />
                        </div>
                      )}

                      {usaColor && (
                        <div className="col-md-12">
                          <label className="form-label">
                            Colores preferidos
                          </label>

                          <div className="border rounded p-2">
                            {coloresDisponibles.length === 0 ? (
                              <div className="text-muted">
                                El producto usa color, pero no tiene colores
                                disponibles cargados.
                              </div>
                            ) : (
                              <div className="row">
                                {coloresDisponibles.map((color) => {
                                  const colorId = getColorId(color);
                                  const checked =
                                    item.coloresPreferidos.includes(colorId);

                                  return (
                                    <div
                                      className="col-md-3 mb-1"
                                      key={colorId}
                                    >
                                      <label className="d-flex align-items-center">
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() =>
                                            toggleColorPreferido(idx, colorId)
                                          }
                                          disabled={saving}
                                          style={{ marginRight: 8 }}
                                        />

                                        {getColorNombre(color)}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {usaColor && (
                        <div className="col-md-4">
                          <label className="form-label">
                            Color entregado
                          </label>
                          <select
                            className="form-control"
                            value={item.colorEntregado}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "colorEntregado",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          >
                            <option value="">Sin definir</option>

                            {coloresDisponibles.map((color) => {
                              const colorId = getColorId(color);

                              return (
                                <option key={colorId} value={colorId}>
                                  {getColorNombre(color)}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      {usaColor && (
                        <div className="col-md-8">
                          <label className="form-label">
                            Observación color
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={item.observacionColor}
                            onChange={(e) =>
                              handleItemChange(
                                idx,
                                "observacionColor",
                                e.target.value
                              )
                            }
                            disabled={saving}
                          />
                        </div>
                      )}

                      <div className="col-md-6">
                        <label className="form-label">Aclaración</label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.aclaracion}
                          onChange={(e) =>
                            handleItemChange(idx, "aclaracion", e.target.value)
                          }
                          disabled={saving}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Observaciones del ítem
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={item.observaciones}
                          onChange={(e) =>
                            handleItemChange(
                              idx,
                              "observaciones",
                              e.target.value
                            )
                          }
                          disabled={saving}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={agregarItem}
              disabled={saving}
            >
              Agregar Producto
            </button>

            <div className="card mt-4">
              <div className="card-header">
                <strong>Resumen</strong>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Productos</div>
                      <strong>{formatUSD(totales.totalProductosUSD)}</strong>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Envío</div>
                      <strong>{formatUSD(totales.totalEnvioUSD)}</strong>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Total Venta</div>
                      <strong>{formatUSD(totales.totalUSD)}</strong>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Comisión Estimada</div>
                      <strong>{formatUSD(totales.totalComisionUSD)}</strong>
                    </div>
                  </div>

                  {totales.tieneBajoMinimo && (
                    <div className="col-md-12">
                      <div className="alert alert-warning py-2 mb-0">
                        Hay productos vendidos por debajo del precio mínimo.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card-footer bg-white border-top">
            {error && (
              <div className="alert alert-danger py-2 mb-2" role="alert">
                {error}
              </div>
            )}

            {ok && (
              <div className="alert alert-success py-2 mb-2" role="alert">
                {ok}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={volver}
                disabled={saving}
              >
                Volver
              </button>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving
                  ? "Guardando..."
                  : esEdicion
                  ? "Guardar Cambios"
                  : "Guardar Venta"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVentaForm;